import { defineStore } from 'pinia'
import { useActivityStore } from './activity'
import { statusMeta } from '~/utils/workflowStatus'
import { useLazyT } from '~/utils/lazyI18n'
import type { ActivityTask, ActivityAction } from '~~/shared/activity/types'
import type { WorkflowRunSummary } from '~~/shared/workflows/workflowRun'
import { DEFAULT_RUN_COMPLETENESS, isDraft, type RunCompleteness } from '~~/shared/workflows/completeness'
import type { I18nMessage } from '~~/shared/workflows/i18nMessage'
import { parseUsageLimitResponse } from '~~/shared/usage/usageLimit'
import { reportUsageLimit } from '~/composables/useUsageLimit'

/**
 * One active run as `POST /api/activity` reports it — MAR-109.
 *
 * Narrower than `WorkflowRunSummary` (`~~/shared/workflows/workflowRun`): the bell needs position,
 * the error payload and the conversation to link back to, and nothing else. It is also camelCase —
 * this shape is assembled from the activity feed, not read off a database row.
 */
export interface ActiveRun {
  id: string
  status: string
  workflowName: string | null
  workflowSlug: string | null
  currentStepIndex: number | null
  stepCount: number
  completedSteps: number
  error: { message?: string; messageI18n?: I18nMessage; stepIndex?: number; retriable?: boolean; usageLimit?: unknown } | null
  conversationId: string | null
  startedAt: string
  completedAt: string | null
  /** Acknowledged server-side; present only while "mostrar descartados" is on. */
  dismissedAt?: string | null
  /** Local only: a cancel was requested but the engine lands it at the next step boundary. */
  cancelRequested?: boolean
  /**
   * How complete this run was ALLOWED to be — the run-level fact the whole of MAR-437 turns on.
   *
   * `draft` means unfilled placeholders were expected, so "Terminado" would be a lie: what came out
   * is a document with blanks in it. Carried from whoever started the run, because the activity feed
   * does not report it yet; a row rehydrated from the server (another tab, a trigger-fired run) has
   * it `undefined` and reads as `strict`, which is the pre-existing behaviour rather than a new one.
   */
  completeness?: RunCompleteness
  /** How many required inputs were blank at launch, for "terminado como borrador, N espacios". */
  blanks?: number
}

/**
 * A run that settled on the gateway's usage-limit block raises the red banner (MAR-1009) — the user
 * need not be on the run page to learn the organization ran out.
 */
function reportUsageLimitOf(run: ActiveRun) {
  const block = parseUsageLimitResponse(run.error?.usageLimit)
  if (block) reportUsageLimit(block)
}

/**
 * Centralized workflow-run state. Backs the runs list (cached so a revisit serves
 * stale-while-revalidate instead of a spinner) and exposes a single-run fetch for
 * the detail page; live streaming itself stays in `useRunStream` (per-view, ephemeral).
 */
export const useWorkflowRunsStore = defineStore('workflow-runs', () => {
  const { authFetch } = useAuthFetch()

  const runs = ref<WorkflowRunSummary[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const loaded = ref(false)

  /**
   * One-shot prefill handoff: a caller drops input values here, keyed by workflow, and the run form
   * consumes them once.
   *
   * TWO producers now. The run detail's "edit & re-run" (which avoids re-fetching a run it already
   * holds), and the chat card handing over what the agent inferred from the conversation (MAR-437).
   * It was called `rerunPrefill` — a name that described its only caller at the time and became
   * wrong the moment chat became the second one.
   */
  const rerunPrefill = ref<{ definitionId: string; inputValues: Record<string, any> } | null>(null)
  function setRunPrefill(definitionId: string, inputValues: Record<string, any>) {
    rerunPrefill.value = { definitionId, inputValues: inputValues ?? {} }
  }
  /** Return + clear the prefill if it belongs to this workflow; else null. */
  function takeRunPrefill(definitionId: string): Record<string, any> | null {
    if (rerunPrefill.value?.definitionId !== definitionId) return null
    const iv = rerunPrefill.value.inputValues
    rerunPrefill.value = null
    return iv
  }

  async function fetchRuns(params: Record<string, string> = {}) {
    if (runs.value.length === 0) loading.value = true
    error.value = null
    try {
      const qs = new URLSearchParams(params).toString()
      const res = await authFetch(`/api/runs${qs ? '?' + qs : ''}`)
      if (!res.ok) throw new Error(await res.text())
      runs.value = await res.json()
      loaded.value = true
    } catch (e: any) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  /** Fetch once on first use; serve cache (revalidating in the background) afterwards. */
  async function ensureRuns(params: Record<string, string> = {}) {
    if (loaded.value) { fetchRuns(params); return }
    await fetchRuns(params)
  }

  async function getRun(id: string): Promise<any> {
    const res = await authFetch(`/api/runs/${id}`)
    // The STATUS is what a caller branches on (the run page turns a 404 into "this run is not
    // there"). The body is Nitro's JSON error envelope — diagnostic, English, never user copy — so it
    // stays out of the message; it used to BE the message, and the run page printed it (MAR-979).
    if (!res.ok) throw Object.assign(new Error(`GET /api/runs/${id} failed with ${res.status}`), { statusCode: res.status })
    return res.json()
  }

  // ── Activity Center producer (MAR-109) ──────────────────────────────────────────────────────────
  //
  // The second producer, after `useDocumentsIndexingStore`. `ActivityKind` has declared `workflow` since the
  // feature shipped, but nothing published it, so a run in the background was visible only on the
  // surface that started it — and a trigger-fired run was visible nowhere.
  //
  // This store owns the projection and the actions; the shared poller (`useActivityFeed`) owns the
  // transport and calls `applyActiveRuns` each tick. All localization happens HERE, because
  // `ActivityTask.detail` is contractually already-localized.

  const activity = useActivityStore()
  /**
   * NOT `const { t } = useI18n()`.
   *
   * A Pinia setup store body runs the first time the store is resolved, and this store is routinely
   * resolved from OUTSIDE a component: the chat card's run handler, the activity poller's tick, a
   * settle listener. `useI18n()` there throws `Must be called at the top of a setup function`, and
   * because the first resolution usually happens inside an async callback the throw arrived as an
   * unhandled rejection that named no file. `useLazyT` defers the lookup to the first `t(...)` call
   * and falls back to the Nuxt app's global composer when there is no component to read.
   */
  const t = useLazyT()

  /** Runs in flight or stopped needing a decision, keyed by id. */
  const activeRuns = ref<ActiveRun[]>([])
  const settleListeners = new Set<(run: ActiveRun) => void>()

  const TERMINAL = ['completed', 'failed', 'cancelled']
  function isTerminal(status: string) { return TERMINAL.includes(status) }

  /** Subscribe to run completion (the chat card's hook). Returns an unsubscribe fn. */
  function onRunSettled(fn: (run: ActiveRun) => void): () => void {
    settleListeners.add(fn)
    return () => settleListeners.delete(fn)
  }

  let requestPoll: (() => void) | null = null
  /** The shared feed registers here. One subscriber; a second would mean two timers again. */
  function onPollRequested(fn: () => void) {
    requestPoll = fn
  }

  /**
   * Show a run the moment it is started, before the first poll tick.
   *
   * Without this the bell stays empty for up to a tick after the user launches something, which reads
   * as "nothing happened" — the exact failure the Activity Center exists to fix.
   */
  function trackRun(run: {
    id: string
    workflowName?: string | null
    conversationId?: string | null
    completeness?: RunCompleteness
    blanks?: number
  }) {
    if (activeRuns.value.some(r => r.id === run.id)) return
    activeRuns.value.push({
      id: run.id,
      status: 'pending',
      completeness: run.completeness ?? DEFAULT_RUN_COMPLETENESS,
      blanks: run.blanks ?? 0,
      workflowName: run.workflowName ?? null,
      workflowSlug: null,
      currentStepIndex: null,
      stepCount: 0,
      completedSteps: 0,
      error: null,
      conversationId: run.conversationId ?? null,
      startedAt: new Date().toISOString(),
      completedAt: null,
    })
    publish()
    // Starting work opens the panel — see the reveal rule in ACTIVITY_CENTER §4d. Launching a
    // workflow counts; the feed DISCOVERING one that was already running does not.
    //
    // EXCEPT when the run was launched from a chat conversation: `WorkflowCard.vue` already reads
    // this same `activeRuns` store and renders the run's live status inline in the chat, so forcing
    // the panel open too is pure redundancy — the user just watched themselves launch it. A run with
    // no `conversationId` (e.g. `WorkflowRunForm.vue` outside chat) has no such surface, so it still
    // needs the reveal.
    if (!run.conversationId) activity.reveal()
    requestPoll?.()
  }

  /**
   * Reconcile the feed for this tick.
   *
   * A tracked run absent from the feed has gone terminal: the endpoint only returns non-terminal and
   * attention rows, so disappearance IS completion. It is fetched once to learn the outcome rather
   * than assumed successful — a run can fail into `producedNothing` (MAR-39) and reporting that as a
   * success is precisely the bug that made the chat agent lie about finished work.
   */
  async function applyActiveRuns(rows: ActiveRun[]) {
    const byId = new Map(rows.map(r => [r.id, r]))
    const vanished = activeRuns.value.filter(r => !byId.has(r.id) && !isTerminal(r.status))
    // Which tracked runs were still in flight BEFORE this tick. A run that reaches a terminal state
    // while STAYING in the feed has settled just as much as one that disappears — and a FAILED run
    // always stays, because the endpoint returns attention rows. Without this, `onRunSettled` fired
    // for successes and never for failures, so the chat card sat on "Ejecutando…" forever.
    const wasRunning = new Set(
      activeRuns.value.filter(r => !isTerminal(r.status)).map(r => r.id),
    )

    activeRuns.value = activeRuns.value
      .filter(r => byId.has(r.id) || isTerminal(r.status))
      .map(r => (byId.has(r.id) ? { ...r, ...byId.get(r.id)! } : r))

    for (const row of rows) {
      if (!activeRuns.value.some(r => r.id === row.id)) activeRuns.value.push(row)
    }

    // Keep the RUNS LIST live off the same tick. `/app/runs` and the recent-runs strip used to run
    // their own 3s poll of `/api/runs` for exactly this (`useRunsAutoRefresh`, now deleted); the feed
    // already carries authoritative status and position, so the list can be patched for free.
    let unknownActive = false
    for (const row of rows) {
      const listed = runs.value.find(r => r.id === row.id)
      if (!listed) { unknownActive = true; continue }
      listed.status = row.status as WorkflowRunSummary['status']
      listed.completed_steps = row.completedSteps
      if (row.stepCount > 0) listed.step_count = row.stepCount
    }
    // A run started somewhere else (another tab, a file-uploaded trigger) is missing from the cached
    // list entirely, and no amount of patching invents the row — so refetch once, on the tick it first
    // appears, rather than on a timer.
    if (unknownActive && loaded.value) fetchRuns()

    for (const gone of vanished) {
      let resolved: ActiveRun = { ...gone, status: 'completed', completedAt: new Date().toISOString() }
      try {
        const full = await getRun(gone.id)
        resolved = { ...resolved, status: full?.status ?? 'completed', error: full?.error ?? null }
      } catch {
        // Tracked by useAuthFetch. Keep the optimistic `completed`: the row links to the run detail,
        // which is authoritative, and stranding it as "running" forever would be worse.
      }
      const idx = activeRuns.value.findIndex(r => r.id === gone.id)
      if (idx >= 0) activeRuns.value[idx] = resolved
      else activeRuns.value.push(resolved)
      reportUsageLimitOf(resolved)
      for (const fn of settleListeners) fn(resolved)
    }

    // Settled in place — reported terminal by the feed rather than by disappearing from it. One
    // notification per run either way: `wasRunning` was captured before the merge, so a run already
    // terminal on the previous tick is not in it, and `vanished` runs are excluded because they are
    // no longer in `rows`.
    for (const row of rows) {
      if (!isTerminal(row.status) || !wasRunning.has(row.id)) continue
      const settled = activeRuns.value.find(r => r.id === row.id) ?? row
      reportUsageLimitOf(settled)
      for (const fn of settleListeners) fn(settled)
    }

    publish()
  }

  function activityState(run: ActiveRun): ActivityTask['state'] {
    if (run.status === 'pending') return 'queued'
    if (run.status === 'executing') return 'running'
    // A failure is the only run outcome that needs a human: retry it, or read why it stopped. A
    // cancel was already the user's decision, so it settles rather than asking again.
    if (run.status === 'failed') return 'attention'
    return 'done'
  }

  function activityDetail(run: ActiveRun): string {
    if (run.cancelRequested && !isTerminal(run.status)) return t('runs.activity.cancelling')
    if (run.status === 'pending') return t('runs.activity.queued')
    if (run.status === 'executing') {
      return run.stepCount > 0
        ? t('runs.stepProgress', { done: run.completedSteps, total: run.stepCount })
        : t('runs.starting')
    }
    if (run.status === 'failed') {
      return (run.error?.messageI18n ? t(run.error.messageI18n.key, run.error.messageI18n.params) : run.error?.message)
        || t('runs.runFailed')
    }
    if (run.status === 'cancelled') return statusLabel(run.status)
    // A draft is NOT "Terminado". It finished, and it finished with holes in it — saying only that
    // it finished is how a document full of `[SALARIO_MENSUAL]` gets filed as a finished contract
    // (MAR-39). The count is what makes the row actionable.
    if (isDraft(run.completeness)) {
      // A number, not a named param: that is what selects the plural branch, and vue-i18n exposes
      // the count back to the message as `{n}`.
      return run.blanks
        ? t('runs.activity.finishedDraftWithBlanks', run.blanks)
        : t('runs.activity.finishedDraft')
    }
    return t('runs.activity.finished')
  }

  /**
   * The status label comes from `WORKFLOW_STATUS_META`, never from a local map.
   *
   * `history.vue` is already the FOURTH copy of run status → copy → colour, and that duplication is
   * the same failure mode that let the raw sentinel `quota:indexing` reach a user. The bell resolves
   * through the same registry as the badge, the header and the step tracker, so it cannot drift.
   */
  function statusLabel(status: string): string {
    const key = statusMeta(status).labelKey
    return key ? t(key) : status
  }

  function activityBadge(run: ActiveRun): ActivityTask['badge'] {
    // Tone is semantic, not a colour — the panel maps it. `statusMeta` owns the *visual* tone for
    // run surfaces, but it returns Tailwind classes, which the ActivityTask contract forbids.
    if (run.status === 'failed') return { text: statusLabel(run.status), tone: 'bad' }
    // `work`, not `good`: a draft landed successfully AND still needs the user. A green "Completado"
    // beside a document with blanks in it is the badge making the same claim the detail refuses to.
    if (run.status === 'completed' && isDraft(run.completeness)) {
      return { text: t('runs.activity.draftBadge'), tone: 'work' }
    }
    if (run.status === 'completed') return { text: statusLabel(run.status), tone: 'good' }
    if (run.status === 'cancelled') return { text: statusLabel(run.status), tone: 'work' }
    return undefined
  }

  function activityActions(run: ActiveRun): ActivityAction[] {
    const actions: ActivityAction[] = []

    // An acknowledged row offers undo and a way to read it — never Cancelar or Reintentar, which
    // would act on work the user has explicitly filed away.
    if (run.dismissedAt) {
      return [
        { id: 'restore', label: t('activity.restore'), kind: 'run' },
        { id: 'view', label: t('runs.activity.view'), kind: 'link', to: `/app/runs/${run.id}` },
      ]
    }

    if (!isTerminal(run.status) && !run.cancelRequested) {
      actions.push({ id: 'cancel', label: t('runs.activity.cancel'), kind: 'run' })
    }
    // `retriable: false` is a run that executed cleanly and produced nothing usable (MAR-39). A retry
    // re-runs the same steps on the same inputs and yields the same nothing, so the honest action is
    // to go read the artifacts explaining why.
    if (run.status === 'failed' && run.error?.retriable !== false) {
      actions.push({ id: 'retry', label: t('runs.activity.retry'), kind: 'run' })
    }
    actions.push({ id: 'view', label: t('runs.activity.view'), kind: 'link', to: `/app/runs/${run.id}` })
    if (run.conversationId) {
      actions.push({ id: 'chat', label: t('runs.activity.viewChat'), kind: 'link', to: `/app/c/${run.conversationId}` })
    }
    if (isTerminal(run.status)) {
      actions.push({ id: 'dismiss', label: t('runs.activity.dismiss'), kind: 'run' })
    }
    return actions
  }

  function toTask(run: ActiveRun): ActivityTask {
    return {
      id: `workflow:${run.id}`,
      kind: 'workflow',
      title: run.workflowName || t('runs.execution'),
      detail: activityDetail(run),
      state: activityState(run),
      // Positions, never a percentage — `completed_steps` of `step_count` is exactly a position, and
      // the step names are not in this feed (the run detail owns those).
      phases: run.status === 'executing' && run.stepCount > 0
        ? Array.from({ length: run.stepCount }, (_, i) => ({
            label: t('runs.stepLabel', { n: i + 1 }),
            done: i < run.completedSteps,
          }))
        : undefined,
      badge: activityBadge(run),
      actions: activityActions(run),
      startedAt: Date.parse(run.startedAt) || Date.now(),
      settledAt: run.completedAt ? Date.parse(run.completedAt) || undefined : undefined,
      dismissed: !!run.dismissedAt,
    }
  }

  /**
   * Push the whole kind. Called imperatively at every mutation point rather than from a watcher:
   * the mutations are all in this file and all explicit, so a watcher would only add a tick of lag
   * and a second place to reason about ordering.
   */
  function publish() {
    activity.replaceKind(
      'workflow',
      activeRuns.value.map(toTask),
      Object.fromEntries(activeRuns.value.flatMap(run => [
        [`workflow:${run.id}:cancel`, () => cancelRun(run.id)],
        [`workflow:${run.id}:retry`, () => retryRun(run.id)],
        [`workflow:${run.id}:dismiss`, () => dismissRun(run.id)],
        [`workflow:${run.id}:restore`, () => restoreRun(run.id)],
      ])),
    )
  }

  /**
   * Cooperative: the engine checks the run status at the top of each step, so the step already in
   * flight finishes first. The row says "Cancelando…" until it lands — promising an instant stop
   * would be a lie the UI can't keep.
   */
  async function cancelRun(runId: string) {
    const run = activeRuns.value.find(r => r.id === runId)
    if (!run) return
    run.cancelRequested = true
    publish()
    try {
      const res = await authFetch(`/api/runs/${runId}/cancel`, { method: 'POST' })
      if (!res.ok) throw new Error(await res.text())
    } catch {
      run.cancelRequested = false
      publish()
    }
  }

  async function retryRun(runId: string) {
    const run = activeRuns.value.find(r => r.id === runId)
    if (!run) return
    run.status = 'pending'
    run.error = null
    run.completedAt = null
    run.cancelRequested = false
    publish()
    requestPoll?.()
    try {
      const res = await authFetch(`/api/runs/${runId}/retry`, { method: 'POST' })
      if (!res.ok) throw new Error(await res.text())
    } catch {
      // The poller reports the real state on the next tick either way.
    }
  }

  /**
   * Acknowledge a settled run.
   *
   * Persisted (MAR-109): a `failed` run is durable, so an in-memory dismissal was re-adopted from
   * `workflow_runs.status` on the next tick. `retryWorkflowRun` clears the flag, so retrying a
   * dismissed run makes it visible again rather than letting it fail a second time in silence.
   */
  function dismissRun(runId: string) {
    activeRuns.value = activeRuns.value.filter(r => r.id !== runId)
    activity.dismiss(`workflow:${runId}`)
    publish()
    authFetch('/api/activity/dismiss', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'workflow', id: runId }),
    }).catch(() => { /* Worst case the row returns on the next tick. */ })
  }

  /** Undo a dismissal, from the "mostrar descartados" list. */
  function restoreRun(runId: string) {
    const run = activeRuns.value.find(r => r.id === runId)
    if (run) run.dismissedAt = null
    publish()
    authFetch('/api/activity/dismiss', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'workflow', id: runId, undo: true }),
    }).catch(() => { /* The next tick reports the real state. */ })
  }

  /** Drop acknowledged rows when the toggle goes off — they are absent from the feed from then on. */
  function purgeDismissed() {
    activeRuns.value = activeRuns.value.filter(r => !r.dismissedAt)
    publish()
  }

  /** Ids the poller must keep asking about. */
  const pendingRunIds = computed(() => activeRuns.value.filter(r => !isTerminal(r.status)).map(r => r.id))

  return {
    runs, loading, error, loaded, fetchRuns, ensureRuns, getRun, setRunPrefill, takeRunPrefill,
    activeRuns, pendingRunIds, trackRun, applyActiveRuns, onRunSettled, onPollRequested,
    cancelRun, retryRun, dismissRun, restoreRun, purgeDismissed,
    // Exposed so any other run surface (e.g. the launch acknowledgment panel) reports the same
    // outcome the Activity Center does, rather than growing its own copy of this branching (#817).
    activityState, activityDetail,
  }
})
